import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import {
  getCallStatus,
  getCustomerNumber,
  normalizeNumber,
  formatTimestamp,
  formatFollowUpDate,
  formatDuration,
} from './callUtils';
import { countryForNumber } from './countryCodes';

const formatMoney = (amount, currency = 'INR') => {
  if (amount == null || amount === '') return '—';
  const symbol = currency === 'INR' ? '₹' : `${currency} `;
  return `${symbol}${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const formatDate = (raw) => {
  if (!raw) return '—';
  const parsed = new Date(String(raw).replace(' ', 'T'));
  return Number.isNaN(parsed.getTime())
    ? raw
    : parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

// Maps an ERP/Shopify status string onto the existing .status pill colors.
const statusPillClass = (value) => {
  const v = (value || '').toLowerCase();
  if (/(paid|delivered|fulfilled|answered|done|completed|converted)/.test(v)) return 'answered';
  if (/(pending|unfulfilled|attempted|progress|transit|shipped)/.test(v)) return 'attempted';
  if (/(cancel|return|refund|missed|fail|not_converted)/.test(v)) return 'missed';
  return 'viewing';
};

const titleCase = (value) =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const greeting = (name) => {
  const first = String(name || '').trim().split(/\s+/)[0];
  return `Hi${first ? ` ${first}` : ''} 👋`;
};

// Ready-to-send WhatsApp text for one ERP order (WhatsApp *bold* markup).
const orderMessage = (order, name) =>
  [
    greeting(name),
    '',
    'Here are your order details:',
    '',
    `🧾 *Order #${order.orderNumber || order.id}*`,
    order.date ? `📅 ${formatDate(order.date)}` : null,
    ...order.products.map((p) => `🛍 ${p.name}${p.qty ? ` × ${p.qty}` : ''}`),
    `💰 Total: *${formatMoney(order.amount)}*`,
    order.paymentStatus ? `💳 Payment: ${titleCase(order.paymentStatus)}` : null,
    order.deliveryStatus ? `🚚 Delivery: ${titleCase(order.deliveryStatus)}` : null,
    '',
    'Thank you for shopping with us! 🙏',
  ]
    .filter((line) => line !== null)
    .join('\n');

// Reminder for an open abandoned checkout, with Shopify's own resume link.
const cartMessage = (cart, name) =>
  [
    greeting(name),
    '',
    'You left some items in your cart:',
    '',
    ...cart.line_items.map((item) => `🛍 ${item.title} × ${item.quantity}`),
    `💰 Total: *${formatMoney(cart.total_price, cart.currency || 'INR')}*`,
    '',
    cart.checkout_url ? `Complete your order here: ${cart.checkout_url}` : 'Reply here and we will help you complete your order.',
  ].join('\n');

const Section = ({ title, count, children }) => (
  <section className="wa-info-section">
    <h4 className="wa-info-section-title">
      {title}
      {count != null && <span className="wa-info-count">{count}</span>}
    </h4>
    {children}
  </section>
);

// Customer info panel beside the WhatsApp chat: who this customer is, their
// ERP orders, call history, follow-ups and abandoned checkouts, so an agent
// can answer without leaving the chat. Server data comes from
// GET /api/whatsapp/customer-info/:number (ERP fetched live each time);
// calls come from the `calls` list App.js already has loaded.
// `onSendToChat(text)` puts a ready-made message into the chat composer
// (the agent reviews it and presses Send).
// showIdentity={false} (the Email page, which shows the contact details
// itself) leaves out the name header and the Profile section.
function WhatsAppCustomerPanel({ customerNumber, calls = [], onSendToChat = null, showIdentity = true }) {
  const { authFetch } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`/api/whatsapp/customer-info/${encodeURIComponent(customerNumber)}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load customer info');
      setInfo(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load customer info');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setInfo(null);
    load();
    // eslint-disable-next-line
  }, [customerNumber]);

  const customerCalls = useMemo(() => {
    const target = normalizeNumber(customerNumber);
    return calls.filter((call) => getCustomerNumber(call) === target);
  }, [calls, customerNumber]);

  const country = countryForNumber(customerNumber);
  const profile = info?.profile;
  const erp = info?.erp;
  const openFollowUps = (info?.followUps || []).filter((f) => f.follow_up_status !== 'done');
  const carts = info?.abandonedCarts || [];
  const answeredCalls = customerCalls.filter((call) => getCallStatus(call) === 'Answered').length;
  const displayName = profile?.name || erp?.customer?.name || null;

  return (
    <aside className="wa-info-panel">
      <div className="wa-info-header">
        {showIdentity ? (
          <div>
            <div className="wa-info-name">{profile?.name || erp?.customer?.name || 'Unknown customer'}</div>
            <div className="wa-info-muted">
              {customerNumber}
              {country && ` · ${country.flag} ${country.name}`}
            </div>
          </div>
        ) : (
          <div className="wa-info-section-title">Orders &amp; activity</div>
        )}
        <button type="button" className="link-btn" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="call-feedback call-feedback-error">{error}</div>}

      {showIdentity && profile && (profile.email || profile.city || profile.state || profile.alt_phone || profile.order_number) && (
        <Section title="Profile">
          <dl className="wa-info-fields">
            {profile.email && (
              <>
                <dt>Email</dt>
                <dd>{profile.email}</dd>
              </>
            )}
            {profile.alt_phone && (
              <>
                <dt>Alt phone</dt>
                <dd>{profile.alt_phone}</dd>
              </>
            )}
            {(profile.city || profile.state) && (
              <>
                <dt>Location</dt>
                <dd>{[profile.city, profile.state].filter(Boolean).join(', ')}</dd>
              </>
            )}
            {profile.order_number && (
              <>
                <dt>Order #</dt>
                <dd>{profile.order_number}</dd>
              </>
            )}
          </dl>
        </Section>
      )}

      <Section title="Orders (ERP)">
        {!info && loading ? (
          <div className="wa-info-muted">Loading…</div>
        ) : erp?.status === 'error' ? (
          <div className="wa-info-muted">Couldn't reach the ERP: {erp.error}</div>
        ) : erp?.status !== 'found' ? (
          <div className="wa-info-muted">No ERP customer for this number.</div>
        ) : (
          <>
            <div className="wa-info-stats">
              <div>
                <span className="wa-info-stat-value">{erp.lifetimeOrders}</span>
                <span className="wa-info-muted">Orders</span>
              </div>
              <div>
                <span className="wa-info-stat-value">{formatMoney(erp.lifetimeValue)}</span>
                <span className="wa-info-muted">Lifetime</span>
              </div>
              <div>
                <span className="wa-info-stat-value">{erp.pendingOrders}</span>
                <span className="wa-info-muted">Pending</span>
              </div>
            </div>
            {(erp.cancelledOrders > 0 || erp.returnedOrders > 0 || erp.customer.customerSince) && (
              <div className="wa-info-muted">
                {erp.customer.customerSince && `Customer since ${formatDate(erp.customer.customerSince)}`}
                {erp.cancelledOrders > 0 && ` · ${erp.cancelledOrders} cancelled`}
                {erp.returnedOrders > 0 && ` · ${erp.returnedOrders} returned`}
              </div>
            )}
            {erp.recentOrders.filter(Boolean).map((order) => (
              <div key={order.id || order.orderNumber} className="wa-info-card">
                <div className="wa-info-card-top">
                  <strong>#{order.orderNumber || order.id}</strong>
                  <span>{formatMoney(order.amount)}</span>
                </div>
                <div className="wa-info-muted">{formatDate(order.date)}</div>
                {order.products.length > 0 && (
                  <div className="wa-info-products">
                    {order.products.map((p, i) => (
                      <span key={i}>
                        {p.name}
                        {p.qty ? ` × ${p.qty}` : ''}
                      </span>
                    ))}
                  </div>
                )}
                <div className="wa-info-pills">
                  {order.paymentStatus && (
                    <span className={`status ${statusPillClass(order.paymentStatus)}`}>{order.paymentStatus}</span>
                  )}
                  {order.deliveryStatus && (
                    <span className={`status ${statusPillClass(order.deliveryStatus)}`}>{order.deliveryStatus}</span>
                  )}
                </div>
                {onSendToChat && (
                  <button
                    type="button"
                    className="wa-info-send"
                    onClick={() => onSendToChat(orderMessage(order, displayName))}
                  >
                    ↗ Send to customer
                  </button>
                )}
              </div>
            ))}
            {erp.links?.ordersUrl && (
              <a className="wa-info-link" href={erp.links.ordersUrl} target="_blank" rel="noreferrer">
                Open in ERP ↗
              </a>
            )}
          </>
        )}
      </Section>

      <Section title="Calls" count={customerCalls.length}>
        {customerCalls.length === 0 ? (
          <div className="wa-info-muted">No calls with this number.</div>
        ) : (
          <>
            <div className="wa-info-muted">
              {answeredCalls} answered · {customerCalls.length - answeredCalls} not answered / attempted
            </div>
            <ul className="wa-info-list">
              {customerCalls.slice(0, 5).map((call) => {
                const status = getCallStatus(call);
                return (
                  <li key={call.uuid || call.id}>
                    <span>
                      {(call.direction || '').toLowerCase().includes('click') ? '↗ Outgoing' : '↙ Incoming'}
                      <span className="wa-info-muted"> · {formatTimestamp(call.start_stamp)}</span>
                    </span>
                    <span className="wa-info-list-right">
                      {status === 'Answered' && <span className="wa-info-muted">{formatDuration(call)}</span>}
                      <span className={`status ${statusPillClass(status)}`}>{status}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </Section>

      <Section title="Open follow-ups" count={openFollowUps.length}>
        {openFollowUps.length === 0 ? (
          <div className="wa-info-muted">Nothing pending.</div>
        ) : (
          <ul className="wa-info-list wa-info-list-stacked">
            {openFollowUps.slice(0, 5).map((followUp) => (
              <li key={followUp.id}>
                <span>
                  <strong>{followUp.follow_up_date ? formatFollowUpDate(followUp.follow_up_date) : 'No date'}</strong>
                  {followUp.follow_up_priority && <span className="wa-info-muted"> · {followUp.follow_up_priority}</span>}
                </span>
                {followUp.follow_up_note && <span className="wa-info-muted">{followUp.follow_up_note}</span>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {carts.length > 0 && (
        <Section title="Abandoned checkouts" count={carts.length}>
          {carts.slice(0, 3).map((cart) => {
            const state = cart.completed_at
              ? 'Completed'
              : cart.resolved_action === 'converted'
              ? `Converted${cart.resolved_order_number ? ` #${cart.resolved_order_number}` : ''}`
              : cart.resolved_action === 'not_converted'
              ? 'Not converted'
              : 'Open';
            return (
              <div key={cart.cart_token} className="wa-info-card">
                <div className="wa-info-card-top">
                  <strong>{formatMoney(cart.total_price, cart.currency || 'INR')}</strong>
                  <span className={`status ${statusPillClass(state === 'Open' ? 'pending' : state)}`}>{state}</span>
                </div>
                <div className="wa-info-muted">{formatDate(cart.checkout_created_at)}</div>
                {cart.line_items.length > 0 && (
                  <div className="wa-info-products">
                    {cart.line_items.map((item, i) => (
                      <span key={i}>
                        {item.title} × {item.quantity}
                      </span>
                    ))}
                  </div>
                )}
                {onSendToChat && state === 'Open' && (
                  <button
                    type="button"
                    className="wa-info-send"
                    onClick={() => onSendToChat(cartMessage(cart, displayName))}
                  >
                    ↗ Send cart reminder
                  </button>
                )}
              </div>
            );
          })}
        </Section>
      )}
    </aside>
  );
}

export default WhatsAppCustomerPanel;
